import { z } from "zod";

// Phase 11 typed configSchema for the `scaling-matrix` template.
// Pure schema source — Plan 02 swaps it into TEMPLATE_CATALOG; this file ships no
// renderer and registers nothing. validateRecipe Pass 2A runs
// configSchema.safeParse(widget.config) so this schema is the config-shape gate a
// recipe's scaling-matrix widget must cross.
//
// Shape rationale (closest analog: size-and-yield.ts). size-and-yield carries ONE
// axis of discrete steps plus a single multiplier and an opaque yield formula.
// scaling-matrix generalizes that to TWO interacting axes, a size-dependent
// integer multiplier with per-axis bounds, and an optional set of predefined
// bulk-mix series. The yield formula stays opaque (length-bounded string, NO
// expression mini-language) exactly as in size-and-yield D-02.
//
// Generality (the C1 grep gate): every field name and every comment here is generic —
// axes / steps / cells / series / multiplier / primary / secondary. Recipe-declared
// step ids carry any domain meaning; the engine never names a recipe's vocabulary.
// Per-axis step shape (id / label / multiplier / optional min,max) is copied
// verbatim from size-and-yield._stepSchema including the min <= max superRefine.
//
// Multiplier bounds are modeled as a generic primary/secondary integer-bound pair
// `{ primary: { min, max, default }, secondary: { min, max, default } }`. The two
// axes can carry different valid integer ranges (one axis may allow a higher count
// than the other); the recipe declares the numeric bounds. No literal axis names
// live in the engine — `primary`/`secondary` are positional, domain-free roles.
//
// predefinedSeries is an optional bounded list of `{ id, label, multiplierPerIndex,
// indexMax }`. Each entry describes a recipe-declared bulk series whose effective
// multiplier is `index * multiplierPerIndex` for an integer index in [1, indexMax].
// multiplierPerIndex is a positive finite number (it may be fractional); indexMax is
// a positive integer bounded for DoS defense.
//
// DoS bounds (T-11-01-01): every array carries .min/.max, every string a .max, and
// every multiplier-count integer a .max — axes <= 4, steps <= 20, predefinedSeries
// <= 10, multiplier min/max/default <= 1000, indexMax <= 20 — mirroring
// size-and-yield's steps.max(20). Bounds an unbounded extracted config before it
// reaches storage.
//
// State (the chosen step per axis, the live multiplier value, the chosen series
// index) is render-side and is intentionally NOT modeled here.
//
// isolatedDeclarations + verbatimModuleSyntax + exactOptionalPropertyTypes (D-15):
// every exported type is an explicit interface and the exported const carries a
// `z.ZodType<ScalingMatrixConfig>` annotation so tsc emits declarations without
// reading across module boundaries (no TS9013).

export interface ScalingMatrixStep {
  id: string;
  label: string;
  multiplier: number;
  min?: number | undefined;
  max?: number | undefined;
}

export interface ScalingMatrixAxis {
  id: string;
  label: string;
  steps: ReadonlyArray<ScalingMatrixStep>;
}

export interface ScalingMatrixBound {
  min: number;
  max: number;
  default: number;
}

export interface ScalingMatrixMultiplierBounds {
  primary: ScalingMatrixBound;
  secondary: ScalingMatrixBound;
}

export interface ScalingMatrixPredefinedSeries {
  id: string;
  label: string;
  multiplierPerIndex: number;
  indexMax: number;
}

// yieldRule is an OPTIONAL generic discriminator selecting how the renderer
// computes a per-pass yield from the resolved axis steps (F-1 → option a).
//   - "primary-times-base" (the legacy default when omitted): baseValue ×
//     primaryStep.multiplier — the size-and-yield back-compatible semantics.
//   - "primary-over-secondary": max(1, round(primaryStep.multiplier × primaryScale
//     / secondaryStep.multiplier)) — a two-axis ratio whose divisor is the
//     secondary step's multiplier. primaryScale is a recipe-declared positive
//     finite scale factor (NOT a literal here — it rides in fixture/recipe DATA).
// All names are positional/domain-free (primary / secondary / scale) — C1-clean.
export type ScalingMatrixYieldRule =
  | { kind: "primary-times-base" }
  | { kind: "primary-over-secondary"; primaryScale: number };

export interface ScalingMatrixYieldDisplay {
  perStepFormula: string;
  unitsField: string;
  baseValue: number;
  yieldRule?: ScalingMatrixYieldRule | undefined;
}

export interface ScalingMatrixConfig {
  axes: ReadonlyArray<ScalingMatrixAxis>;
  multiplier: ScalingMatrixMultiplierBounds;
  predefinedSeries?: ReadonlyArray<ScalingMatrixPredefinedSeries> | undefined;
  yieldDisplay: ScalingMatrixYieldDisplay;
}

// Per-step schema — kebab-case id regex matches the option-set._optionEntrySchema
// pattern so ids stay in one space. multiplier is positive finite (recipes may
// declare 0.25x / 24x steps; no global cap). Per-step `min` is nonnegative, `max`
// is positive; the superRefine enforces min <= max when both are provided — a
// `{ min: 5, max: 2 }` step satisfies the individual bounds but is nonsense and
// would otherwise propagate silently. Copied from size-and-yield._stepSchema.
const _stepSchema = z
  .strictObject({
    id: z
      .string()
      .min(1)
      .max(100)
      .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
    label: z.string().min(1).max(200),
    multiplier: z.number().positive().finite(),
    min: z.number().nonnegative().optional(),
    max: z.number().positive().optional(),
  })
  .superRefine((step, ctx) => {
    if (step.min !== undefined && step.max !== undefined && step.max < step.min) {
      ctx.addIssue({
        code: "custom",
        path: ["max"],
        message: "step.max must be >= step.min",
      });
    }
  });

// Per-axis schema — an axis is a kebab-case id + label + a bounded ordered list of
// discrete steps. steps.min(1): an axis with zero steps is meaningless;
// steps.max(20): bounds recipe complexity (same cap as size-and-yield).
const _axisSchema = z.strictObject({
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  label: z.string().min(1).max(200),
  steps: z.array(_stepSchema).min(1).max(20),
});

// Generic integer-bound triple for one axis's multiplier. `min`/`max` are integers
// (a multiplier count); `max >= min` and `min <= default <= max` are enforced by the
// superRefine so the recipe cannot declare an out-of-range default. `min` is
// nonnegative (an axis may legitimately start at 0 additional units); `max` is
// positive. All three carry .max(1000) — a multiplier count past a few hundred is
// not something a human authors, and the cap bounds the integer magnitude a
// render-time stepper/loop would iterate or allocate against (DoS defense, mirroring
// indexMax's .max(20)).
const _boundSchema = z
  .strictObject({
    min: z.number().int().nonnegative().max(1000),
    max: z.number().int().positive().max(1000),
    default: z.number().int().nonnegative().max(1000),
  })
  .superRefine((bound, ctx) => {
    if (bound.max < bound.min) {
      ctx.addIssue({ code: "custom", path: ["max"], message: "max must be >= min" });
    }
    if (bound.default < bound.min || bound.default > bound.max) {
      ctx.addIssue({
        code: "custom",
        path: ["default"],
        message: "default must be within [min, max]",
      });
    }
  });

// Multiplier bounds: one generic bound triple per axis role (primary / secondary).
const _multiplierSchema = z.strictObject({
  primary: _boundSchema,
  secondary: _boundSchema,
});

// Predefined series — a recipe-declared bulk series whose effective multiplier is
// `index * multiplierPerIndex` for an integer index in [1, indexMax].
// multiplierPerIndex is positive finite (may be fractional); indexMax is a positive
// integer bounded at 20 for DoS defense.
const _seriesSchema = z.strictObject({
  id: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
  label: z.string().min(1).max(200),
  multiplierPerIndex: z.number().positive().finite(),
  indexMax: z.number().int().positive().max(20),
});

// Opaque yield-display formula — length-bounded only. Renderer-side token
// substitution validation is intentionally not the schema's responsibility (D-02).
// baseValue is nonnegative (a recipe may declare 0 for optional output) and finite.
//
// yieldRule is OPTIONAL (omitted ⇒ the renderer applies "primary-times-base",
// back-compat with size-and-yield). It uses z.discriminatedUnion("kind", …) —
// distinct from the OUTER structural WidgetState union (which has no shared
// literal): the two members carry a `kind` z.literal so the discriminated form is
// correct and yields better validation errors. primaryScale is positive + finite
// (a divisor scale factor); strictObject members reject any smuggled extra key,
// and an unknown `kind` is refused — the config-shape Tampering gate (T-11.4.01-01).
const _yieldDisplaySchema = z.strictObject({
  perStepFormula: z.string().min(1).max(500),
  unitsField: z.string().min(1).max(50),
  baseValue: z.number().nonnegative().finite(),
  yieldRule: z
    .discriminatedUnion("kind", [
      z.strictObject({ kind: z.literal("primary-times-base") }),
      z.strictObject({
        kind: z.literal("primary-over-secondary"),
        primaryScale: z.number().positive().finite(),
      }),
    ])
    .optional(),
});

// Top-level schema: axes.min(1).max(4) — at least one sizing axis, capped at 4
// (a matrix beyond a handful of axes is not a grid any human authors). The
// superRefine emits per-index custom issues for duplicate axis ids and, per axis,
// duplicate step ids — mirroring ranked-tier's per-index loop (one ctx.addIssue per
// offence). predefinedSeries.max(10) bounds the optional series list.
export const ScalingMatrixConfigSchema: z.ZodType<ScalingMatrixConfig> = z
  .strictObject({
    axes: z.array(_axisSchema).min(1).max(4),
    multiplier: _multiplierSchema,
    predefinedSeries: z.array(_seriesSchema).max(10).optional(),
    yieldDisplay: _yieldDisplaySchema,
  })
  .superRefine((cfg, ctx) => {
    const seenAxisIds = new Set<string>();
    for (const [i, axis] of cfg.axes.entries()) {
      if (seenAxisIds.has(axis.id)) {
        ctx.addIssue({
          code: "custom",
          path: ["axes", i, "id"],
          message: `duplicate axis id "${axis.id}"`,
        });
      }
      seenAxisIds.add(axis.id);
      const seenStepIds = new Set<string>();
      for (const [j, step] of axis.steps.entries()) {
        if (seenStepIds.has(step.id)) {
          ctx.addIssue({
            code: "custom",
            path: ["axes", i, "steps", j, "id"],
            message: `duplicate step id "${step.id}" in axis "${axis.id}"`,
          });
        }
        seenStepIds.add(step.id);
      }
    }
  });
